import { ReactElement, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { Button } from '../../../components/Button/Button';
import classes from './TenantOverviewPage.module.css';
import { Alert } from '../../../components/Alert/Alert';
import { useGetTenantsQuery } from '../../../hooks/queries/useGetTenantsQuery';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { TenantTable } from '../../../components/TenantTable';
import { Routes } from '../../../enums/Routes';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { FormSelect } from '../../../components/FormSelect/FormSelect';
import { isTrustpulseTenant, isPartnerTenant } from '../../../utils/tenantHelpers';

const PAGE_TITLE = 'Kundeoversikt';
const PAGE_DESCRIPTION = 'Her kan du administrere alle kunder i Trustpulse-systemet';
const ERROR_MESSAGE = 'Kunne ikke laste kunder. Prøv igjen senere.';
const ADD_TENANT_BUTTON = 'Legg til ny kunde';

type FilterOption = 'all' | 'trustpulse' | 'partner';

const FILTER_PREDICATES: Record<FilterOption, (t: import('../../../api/queries').TenantListItem) => boolean> = {
  all: () => true,
  trustpulse: isTrustpulseTenant,
  partner: isPartnerTenant,
};

export const TenantOverviewPage = (): ReactElement => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetTenantsQuery();
  const [partnerFilter, setPartnerFilter] = useState<FilterOption>('all');

  const handleCreateTenant = () => {
    navigate(Routes.SystemAdminCreateTenant);
  };

  const filteredTenants = useMemo(() => {
    if (!data?.tenants) return [];
    return data.tenants.filter(FILTER_PREDICATES[partnerFilter]);
  }, [data?.tenants, partnerFilter]);

  const tenantCounts = useMemo(() => {
    if (!data?.tenants) {
      return { trustpulse: 0, partner: 0, total: 0 };
    }

    return {
      trustpulse: data.tenants.filter(isTrustpulseTenant).length,
      partner: data.tenants.filter(isPartnerTenant).length,
      total: data.total,
    };
  }, [data?.tenants, data?.total]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <div className={classes.headerContent}>
          <div>
            <Heading as="h1">{PAGE_TITLE}</Heading>
            <Paragraph size="lg">{PAGE_DESCRIPTION}</Paragraph>
          </div>
          <Button variant="primary" size="medium" onClick={handleCreateTenant}>
            {ADD_TENANT_BUTTON}
          </Button>
        </div>
      </header>

      <div className={classes.content}>
        {isLoading && <GhostLoader />}

        {error && (
          <Alert variant="danger" size="md">
            <Paragraph>{ERROR_MESSAGE}</Paragraph>
          </Alert>
        )}

        {data && !isLoading && (
          <>
            <div className={classes.statsGrid}>
              <ContainerCard>
                <ContainerCard.Title>Alle kunder</ContainerCard.Title>
                <ContainerCard.Body className={classes.statBody}>
                  <div className={classes.statValue}>{tenantCounts.total}</div>
                </ContainerCard.Body>
              </ContainerCard>

              <ContainerCard>
                <ContainerCard.Title>Trustpulse-kunder</ContainerCard.Title>
                <ContainerCard.Body className={classes.statBody}>
                  <div className={classes.statValue}>{tenantCounts.trustpulse}</div>
                </ContainerCard.Body>
              </ContainerCard>

              <ContainerCard>
                <ContainerCard.Title>Partner-kunder</ContainerCard.Title>
                <ContainerCard.Body className={classes.statBody}>
                  <div className={classes.statValue}>{tenantCounts.partner}</div>
                </ContainerCard.Body>
              </ContainerCard>
            </div>

            <ContainerCard>
              <ContainerCard.Title>Kunder</ContainerCard.Title>
              <ContainerCard.SubHeader>
                <div className={classes.filters}>
                  <FormSelect
                    label="Filtrer etter eier"
                    defaultValue={partnerFilter}
                    onChange={(value) => setPartnerFilter(value as FilterOption)}
                    options={[
                      { label: `Alle kunder (${tenantCounts.total})`, value: 'all' },
                      { label: `Trustpulse (${tenantCounts.trustpulse})`, value: 'trustpulse' },
                      { label: `Partnere (${tenantCounts.partner})`, value: 'partner' },
                    ]}
                  />
                </div>
              </ContainerCard.SubHeader>
              <ContainerCard.Body noPadding>
                <TenantTable tenants={filteredTenants} />
              </ContainerCard.Body>
            </ContainerCard>
          </>
        )}
      </div>
    </div>
  );
};
